import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { useClient } from '@/clients/queries';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { FormDialog } from '@/components/form-dialog';
import { PageShell } from '@/components/page-shell';
import { StatusDot } from '@/components/status-dot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDeleteWarehouse, useUpdateWarehouse, useWarehouse } from '@/warehouses/queries';
import { WarehouseUpdateSchema, type WarehouseUpdateInput } from '@/warehouses/schemas';
import { WarehouseFormFields } from '@/warehouses/warehouse-form';

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

export const WarehouseDetailPage = () => {
  const { warehouseId } = useParams({ from: '/_protected/warehouses/$warehouseId' });
  const navigate = useNavigate();
  const { data: warehouse, isLoading } = useWarehouse(warehouseId);
  const { data: client } = useClient(warehouse?.clientId);
  const updateWarehouse = useUpdateWarehouse(warehouseId);
  const deleteWarehouse = useDeleteWarehouse();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  if (isLoading) return <div className="p-6 text-muted-foreground">Загрузка…</div>;
  if (!warehouse) return <div className="p-6 text-muted-foreground">Склад не найден.</div>;

  const isDeleted = Boolean(warehouse.deletedAt);

  const handleUpdate = (values: WarehouseUpdateInput): void => {
    updateWarehouse.mutate(values, {
      onSuccess: () => {
        toast.success('Склад обновлён');
        setEditOpen(false);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const handleDelete = (): void => {
    deleteWarehouse.mutate(warehouse.id, {
      onSuccess: () => {
        toast.success('Склад удалён');
        setConfirmDeleteOpen(false);
        void navigate({ to: '/warehouses' });
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/warehouses"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Склады
      </Link>
      <PageShell
        title={warehouse.name}
        actions={
          isDeleted ? (
            <Badge variant="destructive">Удалён</Badge>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setEditOpen(true);
                }}
              >
                Редактировать
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirmDeleteOpen(true);
                }}
              >
                Удалить
              </Button>
            </>
          )
        }
      >
        <dl className="grid grid-cols-[200px_1fr] gap-y-3 p-6 text-sm">
          <dt className="text-muted-foreground">ID</dt>
          <dd className="font-mono text-xs">{warehouse.id}</dd>
          <dt className="text-muted-foreground">Клиент</dt>
          <dd>
            <Link
              to="/clients/$clientId"
              params={{ clientId: warehouse.clientId }}
              className="hover:underline"
            >
              {client?.name ?? warehouse.clientId}
            </Link>
          </dd>
          <dt className="text-muted-foreground">Описание</dt>
          <dd>{warehouse.description ?? '—'}</dd>
          <dt className="text-muted-foreground">Статус</dt>
          <dd>
            <StatusDot domain="warehouse" state={warehouse.status} />
          </dd>
          <dt className="text-muted-foreground">Создан</dt>
          <dd>{formatDate(warehouse.createdAt)}</dd>
          <dt className="text-muted-foreground">Обновлён</dt>
          <dd>{formatDate(warehouse.updatedAt)}</dd>
        </dl>
      </PageShell>

      <FormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Редактирование склада"
        schema={WarehouseUpdateSchema}
        defaultValues={{
          name: warehouse.name,
          description: warehouse.description ?? '',
          status: warehouse.status,
        }}
        onSubmit={handleUpdate}
        isPending={updateWarehouse.isPending}
      >
        <WarehouseFormFields mode="edit" />
      </FormDialog>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Удалить склад?"
        description={`Склад «${warehouse.name}» будет soft-deleted.`}
        onConfirm={handleDelete}
        isPending={deleteWarehouse.isPending}
      />
    </div>
  );
};
